/**
 * HTTP client.
 *
 * Thin on purpose. Everything it does exists for a reason the caller should not
 * have to remember:
 *
 *  - `credentials: 'include'` so the session cookie travels, and the cookie is
 *    the only place a token lives — this module never sees one.
 *  - The CSRF marker header on every state-changing request, because the API
 *    refuses without it.
 *  - Errors become a typed `ApiError` carrying the server's stable code, so UI
 *    code branches on a code rather than on message text that will be
 *    translated tomorrow.
 */

/**
 * Endpoints.
 *
 * In production the web app, the API, and the gateway are served from one
 * origin behind nginx. That is not a deployment convenience — it removes CORS
 * from the threat model entirely, and it is what allows the `__Host-` cookie
 * prefix, which forbids any subdomain from setting a session cookie for the
 * parent. Separate subdomains would require credentialed cross-origin requests
 * and give up both.
 *
 * Empty base = same origin, which is now what development uses too: the dev
 * server proxies `/v1`, `/gateway` and `/media` the way nginx does. Pointing
 * the client at another port was the only thing that made development
 * cross-origin, and cross-origin is a different browser to build against — see
 * the note in `.env.development`.
 *
 * The VITE_* overrides remain for the one case that needs them: a local UI
 * driving an API on another machine. Empty counts as unset, because `??` does
 * not — and an empty gateway URL is a socket that connects to nothing.
 */
const override = (value: unknown): string | undefined =>
  typeof value === 'string' && value !== '' ? value : undefined;

export const API_BASE = override(import.meta.env['VITE_API_BASE']) ?? '';

export const GATEWAY_URL =
  override(import.meta.env['VITE_GATEWAY_URL']) ??
  `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/gateway`;

export type ApiErrorCode =
  | 'BAD_REQUEST'
  | 'VALIDATION_FAILED'
  | 'UNAUTHENTICATED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'RATE_LIMITED'
  | 'PAYLOAD_TOO_LARGE'
  | 'INTERNAL'
  | 'NETWORK';

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;
  readonly retryAfter: number | undefined;

  constructor(code: ApiErrorCode, message: string, status: number, retryAfter?: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      // Without this the cookie is not sent and every request is anonymous —
      // a failure that looks like a broken session rather than a missing flag.
      credentials: 'include',
      headers: {
        ...(options.body !== undefined ? { 'content-type': 'application/json' } : {}),
        // A cross-site request cannot set this without a preflight the API
        // will refuse, which is what makes it a CSRF defence.
        ...(method !== 'GET' ? { 'x-aksolot-request': '1' } : {}),
      },
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal !== undefined ? { signal: options.signal } : {}),
    });
  } catch (error) {
    // Offline, DNS failure, or the request was aborted. Distinguished from a
    // server error so the UI can say "you appear to be offline" truthfully.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError('NETWORK', 'Bağlantı kurulamadı.', 0);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text === '' ? null : safeParse(text);

  if (!response.ok) {
    const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
    const retryAfter = response.headers.get('retry-after');
    throw new ApiError(
      (error?.code as ApiErrorCode) ?? 'INTERNAL',
      error?.message ?? 'Bir şeyler ters gitti.',
      response.status,
      retryAfter === null ? undefined : Number(retryAfter),
    );
  }

  return payload as T;
}

function safeParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Resource types — mirror the API's response shapes
// ---------------------------------------------------------------------------

export interface SessionUser {
  id: string;
  username: string;
}

export interface ServerSummary {
  id: string;
  name: string;
  ownerId: string;
}

export interface ChannelSummary {
  id: string;
  kind: 'text' | 'voice' | 'announcement' | 'forum' | 'category' | 'thread';
  name: string;
  position: number;
  topic: string | null;
  /** Seconds between messages per member. 0 disables slow mode. */
  rateLimitPerUser?: number;
  nsfw?: boolean;
}

export interface RoleRow {
  id: string;
  name: string;
  position: number;
  /** Decimal string: a 64-bit mask does not survive a JS number. */
  permissions: string;
  color: number | null;
  /** Shown in its own member-list group rather than merged with everyone else. */
  hoist: boolean;
  mentionable: boolean;
  /** Owned by an integration: the editor disables rather than hides its controls. */
  managed: boolean;
  isEveryone: boolean;
}

export interface BanRow {
  userId: string;
  name: string | null;
  moderatorId: string | null;
  moderatorName: string | null;
  reason: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface AuditEntry {
  id: string;
  actorId: string | null;
  /** Never blank: a system action reads as "sistem", not as an empty name. */
  actorName: string;
  action: string;
  targetKind: string | null;
  targetId: string | null;
  reason: string | null;
  changes: Record<string, unknown>;
  createdAt: string;
}

export interface InviteRow {
  code: string;
  creatorId: string | null;
  creatorName: string | null;
  uses: number;
  maxUses: number | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface Attachment {
  id: string;
  filename: string;
  /** Decided by the server from the first bytes, never from the upload's claim. */
  contentType: string;
  byteSize: number;
  /** Present for images the server could measure; the renderer reserves the box. */
  width: number | null;
  height: number | null;
}

export interface Message {
  id: string;
  channelId: string;
  authorId: string | null;
  kind: string;
  content: string;
  replyToId: string | null;
  mentions: string[];
  mentionsEveryone: boolean;
  pinned: boolean;
  editedAt: string | null;
  createdAt: string;
  attachments: Attachment[];
  /**
   * Already counted, and already answered for you.
   *
   * The server aggregates because the interface only ever asks two questions —
   * how many, and am I one of them — and shipping one row per person so the
   * client can count them itself would also hand everyone the list of who
   * reacted to what, which is not something this product needs to publish.
   */
  reactions: Reaction[];
}

export interface Reaction {
  emoji: string;
  count: number;
  reacted: boolean;
}

export interface UserProfile {
  id: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
}

/** A folder in the space rail. Personal: two people file the same spaces differently. */
export interface SpaceFolder {
  id: string;
  name: string | null;
  collapsed: boolean;
  /** Spaces in it, in draw order. Spaces the account has left are absent. */
  serverIds: string[];
}

/** The same thing on the way out, where a folder that does not exist yet has no id. */
export type SpaceFolderInput = Omit<SpaceFolder, 'id'> & { id?: string };

/**
 * What the server is willing to say about someone's presence.
 *
 * `invisible` is deliberately absent: the API reports an invisible person as
 * `offline` and never tells the client the difference, so a client-side type
 * that could hold it would be describing a state that cannot arrive.
 */
export type PresenceState = 'online' | 'idle' | 'dnd' | 'offline';

export interface ServerMember extends UserProfile {
  nickname: string | null;
  roleIds: string[];
  joinedAt: string;
  timedOutUntil: string | null;
  /**
   * Whether this person has a socket attached right now.
   *
   * Already filtered by their own visibility setting: someone who chose to be
   * invisible reads as offline here, and the client is never told the
   * difference. There is nothing to leak by accident because nothing arrives.
   */
  online: boolean;
  /**
   * Which presence, not merely whether.
   *
   * The endpoint has always sent this and the client threw it away, so idle and
   * do-not-disturb were invisible everywhere: the member list drew a hardcoded
   * green dot and changing your status appeared to do nothing at all.
   */
  status: PresenceState;
}

export interface ActiveSession {
  id: string;
  current: boolean;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  userAgent: string | null;
  country: string | null;
}

/**
 * What a sign-in produced.
 *
 * Two shapes rather than one with optional fields: an account with a second
 * factor yields a challenge and *no* user, and modelling that as
 * `{ user?, challenge? }` invites a caller to read `user` on the branch where
 * it was never there. The discriminant makes the branch impossible to skip.
 */
export type LoginResult =
  | { mfaRequired: true; challenge: string; expiresInMs: number }
  | { mfaRequired?: false; user: SessionUser };

export interface TwoFactorState {
  enabled: boolean;
  /** Setup was started and never finished. Protects nothing yet. */
  pending: boolean;
  recoveryCodesRemaining: number;
}

export const auth = {
  register: (username: string, password: string) =>
    api<{ user: SessionUser }>('/v1/auth/register', {
      method: 'POST',
      body: { username, password },
    }),

  login: (username: string, password: string) =>
    api<LoginResult>('/v1/auth/login', {
      method: 'POST',
      body: { username, password },
    }),

  /** The second half of a sign-in. Takes a TOTP code or a recovery code. */
  completeMfa: (challenge: string, code: string) =>
    api<{ user: SessionUser; recoveryCodesRemaining?: number }>('/v1/auth/login/mfa', {
      method: 'POST',
      body: { challenge, code },
    }),

  logout: () => api<void>('/v1/auth/logout', { method: 'POST' }),

  sessions: () => api<{ sessions: ActiveSession[] }>('/v1/auth/sessions'),

  // ---------------------------------------------------------------------------
  // Address verification and password recovery
  // ---------------------------------------------------------------------------

  setEmail: (email: string) =>
    api<{ sent: boolean }>('/v1/auth/email', { method: 'POST', body: { email } }),

  verifyEmail: (code: string) =>
    api<void>('/v1/auth/email/verify', { method: 'POST', body: { code } }),

  changePassword: (current: string, password: string) =>
    api<void>('/v1/auth/password/change', { method: 'POST', body: { current, password } }),

  /**
   * Always resolves for any well-formed request, whether or not the account
   * exists — the server answers uniformly on purpose, and a client that
   * inferred otherwise would rebuild the membership oracle in the UI.
   */
  forgotPassword: (username: string) =>
    api<{ sent: boolean }>('/v1/auth/password/forgot', {
      method: 'POST',
      body: { username },
    }),

  resetPassword: (username: string, code: string, password: string) =>
    api<void>('/v1/auth/password/reset', {
      method: 'POST',
      body: { username, code, password },
    }),

  // ---------------------------------------------------------------------------
  // Two-factor authentication
  // ---------------------------------------------------------------------------

  twoFactor: () => api<TwoFactorState>('/v1/auth/2fa'),

  /** Mints a secret. Shown once, and inert until `enableTwoFactor` succeeds. */
  setupTwoFactor: () =>
    api<{ secret: string; uri: string }>('/v1/auth/2fa/setup', { method: 'POST' }),

  enableTwoFactor: (code: string) =>
    api<{ recoveryCodes: string[] }>('/v1/auth/2fa/enable', { method: 'POST', body: { code } }),

  disableTwoFactor: (password: string) =>
    api<void>('/v1/auth/2fa/disable', { method: 'POST', body: { password } }),

  regenerateRecoveryCodes: (code: string) =>
    api<{ recoveryCodes: string[] }>('/v1/auth/2fa/recovery-codes', {
      method: 'POST',
      body: { code },
    }),
};

export const users = {
  me: () =>
    api<UserProfile & { email: string | null; emailVerified: boolean; locale: string }>(
      '/v1/users/@me',
    ),

  /**
   * Batch resolution. One request per distinct author in a page, rather than
   * one request *per* author — fifty round trips to paint one screen, and a
   * request pattern that itself leaks which ids the client is curious about.
   */
  resolve: (ids: readonly string[]) =>
    ids.length === 0
      ? Promise.resolve({ users: [] as UserProfile[] })
      : api<{ users: UserProfile[] }>(`/v1/users?ids=${ids.join(',')}`),

  /**
   * Uploads a profile picture.
   *
   * Multipart, so no JSON content type — the browser writes the boundary. Stills
   * are downscaled before they leave (see `downscaleImage`); GIFs are sent as
   * they are, because re-encoding one in a canvas keeps the first frame and
   * throws the animation away.
   */
  uploadAvatar: async (file: File): Promise<{ avatarUrl: string }> => {
    const form = new FormData();
    form.append('file', file, file.name);
    const response = await fetch(`${API_BASE}/v1/users/@me/avatar`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'x-aksolot-request': '1' },
      body: form,
    });
    const payload: unknown = safeParse(await response.text());
    if (!response.ok) {
      const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
      throw new ApiError(
        (error?.code as ApiErrorCode) ?? 'INTERNAL',
        error?.message ?? 'Fotoğraf yüklenemedi.',
        response.status,
      );
    }
    return payload as { avatarUrl: string };
  },

  removeAvatar: () => api<void>('/v1/users/@me/avatar', { method: 'DELETE' }),

  /** The rail's folders. Private to the account; there is no route for anyone else's. */
  spaceLayout: () => api<{ folders: SpaceFolder[] }>('/v1/users/@me/space-layout'),

  /**
   * Writes the whole arrangement.
   *
   * One request for the entire rail, because every real edit moves several
   * things at once — see the note on the server side. The response is the
   * stored layout, so newly created folders come back with their ids.
   */
  saveSpaceLayout: (folders: SpaceFolderInput[]) =>
    api<{ folders: SpaceFolder[] }>('/v1/users/@me/space-layout', {
      method: 'PUT',
      body: { folders },
    }),
};

export const servers = {
  list: () => api<{ servers: ServerSummary[] }>('/v1/servers'),

  create: (name: string) =>
    api<{ id: string; name: string; defaultChannelId: string }>('/v1/servers', {
      method: 'POST',
      body: { name },
    }),

  channels: (serverId: string) =>
    api<{ channels: ChannelSummary[] }>(`/v1/servers/${serverId}/channels`),

  createInvite: (serverId: string, options: { maxUses?: number; maxAgeSeconds?: number } = {}) =>
    api<{ code: string; maxUses: number | null; maxAgeSeconds: number | null }>(
      `/v1/servers/${serverId}/invites`,
      { method: 'POST', body: options },
    ),

  /** What a code leads to, before committing to it. */
  peekInvite: (code: string) =>
    api<{ serverId: string; name: string; memberCount: number }>(`/v1/invites/${code}`),

  redeemInvite: (code: string) =>
    api<{ serverId: string }>(`/v1/invites/${code}`, { method: 'POST' }),

  createChannel: (serverId: string, name: string, kind: 'text' | 'voice') =>
    api<{ id: string; name: string; kind: string }>(`/v1/servers/${serverId}/channels`, {
      method: 'POST',
      body: { name, kind },
    }),

  /**
   * Partial channel edit.
   *
   * Fields left out are left alone; `topic: null` clears it. The distinction
   * is the whole contract — a client that sends the full object every time
   * would overwrite a field someone else changed between load and save.
   */
  updateChannel: (
    channelId: string,
    patch: { name?: string; topic?: string | null; rateLimitPerUser?: number; nsfw?: boolean },
  ) => api<ChannelSummary>(`/v1/channels/${channelId}`, { method: 'PATCH', body: patch }),

  deleteChannel: (channelId: string) =>
    api<void>(`/v1/channels/${channelId}`, { method: 'DELETE' }),

  invites: (serverId: string) =>
    api<{ invites: InviteRow[] }>(`/v1/servers/${serverId}/invites`),

  revokeInvite: (code: string) => api<void>(`/v1/invites/${code}`, { method: 'DELETE' }),

  // ---- Administration ------------------------------------------------------

  rename: (serverId: string, name: string) =>
    api<{ id: string; name: string }>(`/v1/servers/${serverId}`, {
      method: 'PATCH',
      body: { name },
    }),

  destroy: (serverId: string) => api<void>(`/v1/servers/${serverId}`, { method: 'DELETE' }),

  roles: (serverId: string) => api<{ roles: RoleRow[] }>(`/v1/servers/${serverId}/roles`),

  createRole: (serverId: string, name: string) =>
    api<RoleRow>(`/v1/servers/${serverId}/roles`, { method: 'POST', body: { name } }),

  updateRole: (
    serverId: string,
    roleId: string,
    patch: {
      name?: string;
      permissions?: string;
      color?: number | null;
      hoist?: boolean;
      mentionable?: boolean;
    },
  ) =>
    api<RoleRow>(`/v1/servers/${serverId}/roles/${roleId}`, { method: 'PATCH', body: patch }),

  deleteRole: (serverId: string, roleId: string) =>
    api<void>(`/v1/servers/${serverId}/roles/${roleId}`, { method: 'DELETE' }),

  addMemberRole: (serverId: string, userId: string, roleId: string) =>
    api<void>(`/v1/servers/${serverId}/members/${userId}/roles/${roleId}`, { method: 'PUT' }),

  removeMemberRole: (serverId: string, userId: string, roleId: string) =>
    api<void>(`/v1/servers/${serverId}/members/${userId}/roles/${roleId}`, { method: 'DELETE' }),

  kick: (serverId: string, userId: string, reason?: string) =>
    api<void>(`/v1/servers/${serverId}/members/${userId}`, {
      method: 'DELETE',
      body: reason === undefined ? {} : { reason },
    }),

  /** `minutes: 0` lifts the timeout — one endpoint, one meaning. */
  timeout: (serverId: string, userId: string, minutes: number, reason?: string) =>
    api<{ timedOutUntil: string | null }>(
      `/v1/servers/${serverId}/members/${userId}/timeout`,
      { method: 'PUT', body: reason === undefined ? { minutes } : { minutes, reason } },
    ),

  bans: (serverId: string) => api<{ bans: BanRow[] }>(`/v1/servers/${serverId}/bans`),

  ban: (serverId: string, userId: string, options: { reason?: string; days?: number } = {}) =>
    api<void>(`/v1/servers/${serverId}/bans/${userId}`, { method: 'PUT', body: options }),

  unban: (serverId: string, userId: string) =>
    api<void>(`/v1/servers/${serverId}/bans/${userId}`, { method: 'DELETE' }),

  auditLog: (serverId: string, options: { before?: string; action?: string } = {}) => {
    const query = new URLSearchParams();
    if (options.before !== undefined) query.set('before', options.before);
    if (options.action !== undefined) query.set('action', options.action);
    const suffix = query.size === 0 ? '' : `?${query.toString()}`;
    return api<{ entries: AuditEntry[]; nextBefore: string | null }>(
      `/v1/servers/${serverId}/audit-log${suffix}`,
    );
  },

  /** Who is in a voice channel right now. Ephemeral; never a stored history. */
  voiceParticipants: (channelId: string) =>
    api<{ participants: string[] }>(`/v1/channels/${channelId}/voice`),

  members: (serverId: string) =>
    api<{ members: ServerMember[]; nextAfter: string | null; total: number }>(
      `/v1/servers/${serverId}/members`,
    ),
};

export const channels = {
  /**
   * Rewrites the order of a server's channels.
   *
   * The whole list rather than one channel's position: dragging a row moves
   * every row below it, and a request per moved channel leaves the sidebar
   * half-reordered the moment one of them fails.
   */
  reorder: (serverId: string, channelIds: string[]) =>
    api<void>(`/v1/servers/${serverId}/channels/order`, {
      method: 'PUT',
      body: { channelIds },
    }),
};

export const messages = {
  /**
   * Toggling, expressed as two verbs.
   *
   * PUT and DELETE rather than one POST that flips, because the client already
   * knows which way it is going — `reacted` came down with the message — and a
   * flip endpoint makes two devices pressing at once land on whichever order
   * the server happened to see, which is how a reaction ends up removed by the
   * person who was adding it.
   */
  react: (channelId: string, messageId: string, emoji: string): Promise<void> =>
    api<void>(
      `/v1/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
      { method: 'PUT' },
    ),
  unreact: (channelId: string, messageId: string, emoji: string): Promise<void> =>
    api<void>(
      `/v1/channels/${channelId}/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
      { method: 'DELETE' },
    ),

  list: (channelId: string, before?: string) =>
    api<{ messages: Message[]; nextBefore: string | null }>(
      `/v1/channels/${channelId}/messages${before === undefined ? '' : `?before=${before}`}`,
    ),

  send: (
    channelId: string,
    content: string,
    replyToId?: string | null,
    attachmentIds: string[] = [],
  ) =>
    api<Message>(`/v1/channels/${channelId}/messages`, {
      method: 'POST',
      // Omitted rather than sent as null when there is no reply: the route
      // distinguishes absent from null elsewhere, and this keeps the request
      // saying only what it means.
      body: {
        content,
        ...(replyToId == null ? {} : { replyToId }),
        ...(attachmentIds.length === 0 ? {} : { attachmentIds }),
      },
    }),

  /**
   * Signals that the user is composing. Fire-and-forget on purpose: a failed
   * typing notice is not worth an error path, and retrying it would send a
   * stale claim.
   */
  typing: (channelId: string) =>
    api<void>(`/v1/channels/${channelId}/typing`, { method: 'POST' }),

  remove: (channelId: string, messageId: string) =>
    api<void>(`/v1/channels/${channelId}/messages/${messageId}`, { method: 'DELETE' }),

  /** Idempotent on both sides: pinning what is already pinned is a no-op, not an error. */
  setPinned: (channelId: string, messageId: string, pinned: boolean) =>
    api<void>(`/v1/channels/${channelId}/messages/${messageId}/pin`, {
      method: pinned ? 'PUT' : 'DELETE',
    }),

  pins: (channelId: string) => api<{ messages: Message[] }>(`/v1/channels/${channelId}/pins`),
};

/**
 * Where the bytes of an attachment live.
 *
 * A plain URL rather than a fetch: `<img src>` and `<a href>` want one, and the
 * route is cookie-authenticated on the same origin, so the browser sends the
 * session for a subresource load exactly as it does for an XHR. There is no
 * signed-URL step to forget, and no window in which a link keeps working after
 * the reader loses access — the route re-checks the channel on every request.
 */
export function attachmentUrl(id: string): string {
  return `${API_BASE}/v1/attachments/${id}`;
}

/** 25 MiB. The API rejects anything larger; this is so it can be said sooner. */
export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

/** Per message, restated from the API. */
export const MAX_ATTACHMENTS = 10;

export const attachments = {
  /**
   * Uploads one file and returns the pending row it created.
   *
   * `XMLHttpRequest`, not `fetch`, and the reason is the progress events: a
   * fetch upload body is opaque until it finishes, so a 25 MB file on a slow
   * connection would be a frozen composer for a minute with nothing moving.
   * That is the whole difference between "uploading" and "broken" to the person
   * waiting.
   *
   * No `content-type` is set: the browser has to write the multipart boundary
   * into it, and a hand-written header without one makes the parser fail on a
   * body that is perfectly well formed.
   */
  upload(
    channelId: string,
    file: File,
    options: { onProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
  ): Promise<Attachment> {
    return new Promise<Attachment>((resolve, reject) => {
      const form = new FormData();
      form.append('file', file, file.name);

      const request = new XMLHttpRequest();
      request.open('POST', `${API_BASE}/v1/channels/${channelId}/attachments`);
      request.withCredentials = true;
      request.setRequestHeader('x-aksolot-request', '1');

      request.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && event.total > 0) {
          options.onProgress?.(event.loaded / event.total);
        }
      });

      request.addEventListener('load', () => {
        const payload: unknown = safeParse(request.responseText);
        if (request.status >= 200 && request.status < 300) {
          resolve(payload as Attachment);
          return;
        }
        const error = (payload as { error?: { code?: string; message?: string } } | null)?.error;
        reject(
          new ApiError(
            (error?.code as ApiErrorCode) ?? 'INTERNAL',
            error?.message ?? 'Dosya yüklenemedi.',
            request.status,
          ),
        );
      });

      request.addEventListener('error', () =>
        reject(new ApiError('NETWORK', 'Bağlantı kurulamadı.', 0)),
      );
      request.addEventListener('abort', () =>
        reject(new DOMException('Upload aborted', 'AbortError')),
      );

      if (options.signal !== undefined) {
        if (options.signal.aborted) {
          reject(new DOMException('Upload aborted', 'AbortError'));
          return;
        }
        options.signal.addEventListener('abort', () => request.abort(), { once: true });
      }

      request.send(form);
    });
  },
};
